import 'reflect-metadata';
import type { interfaces } from 'inversify';
import { Container } from 'inversify';
import { AppController } from '@/engine/app/App.controller';
import { IFrameController } from '@/engine/app/IFrame.controller';

import type {
  IViewFactoryProvider,
  IIlluminator,
  IViewPortControllerFactory,
  JSONConfig,
  IViewPortManagerBuilder,
  ITwoDLayerController,
  IMeasurementsBoxesController,
  IViewItemsViewModelController,
  IObjectBoxesController,
  ISceneItemDetailsFactory,
  ICineloopInfo,
  INotificationsCenter
} from '@/engine/app/interfaces';
import type {
  ICubeButtonsController,
  ICubePresentationBuilder,
  ICubeSceneController,
  ICubeControllerFactory
} from '@/components/cubeControl/interfaces'; //TODO: [chebotarev] move CubeControl to separate config
import type { IFluoroAnglesCalculatorFactory } from '@/components/fluoroAngles/interfaces'; //TODO: [chebotarev] move it to separate config
import type { INavigationControllerFactory } from '@/components/navigationControl/interfaces'; //TODO: [frances] talk to Anton about previous ToDo
import { ViewController } from '@/engine/app/View.controller';
import { ViewFactoryProvider } from '@/engine/app/ViewFactory.provider';
import { ViewsService } from '@/engine/app/Views.service';
import { ViewsListController } from '@/engine/app/ViewsList.controller';
import { ObjectsListController } from '@/components/objectsList';
import { Illuminator } from '@/components/viewPort/illuminator';
import { ViewPortControllerFactory } from '@/engine/app/ViewPortController.factory';
import { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { TYPES } from './types';
import { TYPES as AnatomyOrientationTYPES } from '@/components/cubeControl/anatomyOrientationControl/types';
import { TYPES as CubeTYPES } from '@/components/cubeControl/types';
import { TYPES as FluoroAngleTYPES } from '@/components/fluoroAngles/types';
import { TYPES as FluoroCurveTYPES } from '@/components/fluoroCurve/types';
import { TYPES as NavigationTYPES } from '@/components/navigationControl/types';
import { imagesModule } from '@/engine/app/modules/images';
import { reportingModule } from '@/engine/app/modules/reporting';
import type { IWasmModulesRegistry } from '../WasmModules';
import { WasmModulesRegistry } from '../WasmModules';
import { ImageSlicesListController } from '@/components/imageSlicesList';
import { ViewPortManagerBuilder } from '@/components/viewPort/viewPortManagerBuilder';
import { indicationModule } from '@/engine/app/modules/indications';
import { httpModule } from '@/engine/app/modules/http';
import { CaseInfoApi } from '@/engine/app/CaseInfoApi';
import { AppBootstrapper } from '@/engine/app/AppBootstrapper';
import { AuthInterceptor } from '@/engine/app/AuthInterceptor';
import { TwoDLayerController } from '@/components/viewPort/TwoDLayerController';
import { CaseFilesApi } from '@/engine/app/CaseFilesApi';
import type { ICaseDataRepositoryFactory } from '@/engine/app/CaseDataProvider';
import { CaseDataProvider } from '@/engine/app/CaseDataProvider';
import { MeasurementBoxesController } from '@/components/boxes/measurementBoxes';
import { IndicationsBoxController } from '@/components/boxes/indicationsBoxes/indicationBoxController.ts';
import { StepsListController } from '@/components/stepsList/StepsList.controller';
import { ApproveController } from '@/components/approve/Approve.controller';
import { CubeButtonsController } from '@/components/cubeControl/CubeButtonsController';
import { CubePresentationBuilder } from '@/components/cubeControl/CubePresentationBuilder';
import { CubeSceneController } from '@/components/cubeControl/CubeSceneController';
import { CubeControllerFactory } from '@/components/cubeControl/CubeControllerFactory';
import { NavigationControllerFactory } from '@/components/navigationControl/NavigationControllerFactory';
import { ViewOptionsController } from '@/components/viewOption';
import { FluoroAnglesCalculatorFactory } from '@/components/fluoroAngles/FluoroAnglesCalculatorFactory';
import type { IAnatomyOrientationController } from '@/components/cubeControl/anatomyOrientationControl/interfaces';
import { AnatomyOrientationController } from '@/components/cubeControl/anatomyOrientationControl/AnatomyOrientationController';
import { ViewItemsViewModelController } from '@/engine/app/ViewItemsViewModel.controller';
import { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';
import { OrientationPresetsController } from '@/components/orientationPresets/OrientationPresets.controller';
import { CasePersistenceHub } from '@/engine/app/CasePersistance/CasePersistenceHub';
import { CurvedMprController } from '@/components/curvedMpr/CurvedMprController';
import { IndicationsResolver } from '@/engine/app/IndicationsResolver';
import { CasePersistenceRegistrar } from '@/engine/app/CasePersistance/CasePersistenceRegistrar';
import { OrientationPresetsResolver } from '@/engine/app/OrientationPresetsResolver';
import { AreaDiametersController } from '@/components/boxes/measurementBoxes/AreaDiameters.controller';
import { DiameterMeasurementsResolver } from '@/engine/app/DiameterMeasurementsResolver';
import { SavingStateController } from '@/engine/app/SavingState.controller';
import { FluoroCurveControllerFactory } from '@/components/fluoroCurve/FluoroCurveControllerFactory';
import type { IFluoroCurveControllerFactory } from '@/components/fluoroCurve/interfaces';
import { PointsSwitcherController } from '@/components/PointsSwitcher/PointsSwitcher.controller';
import { ObjectBoxesController } from '@/components/boxes/objectBoxes/ObjectBoxes.controller';
import { SurgeryRegistry } from '@/engine/app/registries/SurgeryRegistry.ts';
import { PresentationCreatorsRegistry } from '@/engine/app/registries/PresentationCreatorsRegistry.ts';
import { ImporterFactory } from '../Serialization/Importer.factory';
import type { ICineloopControlController, IPhasesCurveController } from '@/components/phasesCurve/interfaces';
import { PhasesCurveController } from '@/components/phasesCurve/phasesCurveController';
import { PlanningEntityRegistry } from '../DynamicPlanning/PlanningEntityRegistry';
import { DynamicPlanningAlgorithmPromoter } from '../DynamicPlanning/DynamicPlanningAlgorithmPromoter';
import { UIConfigResolver } from '@/engine/app/modules/ui/UIConfigResolver';
import { SceneItemDetailsFactory } from '../app/SceneItemDetailsFactory';
import { LoadStateStrategiesProvider, type IStrategyByKindProvider } from '../app/LoadStateStrategiesProvider';
import { VariableDistancesBoxController } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/BoxController/VariableDistancesBox.controller.ts';
import { DistanceMeasurementsResolver } from '../app/DistanceMeasurementsResolver';
import type { ICustomCommissurePointsBoxController } from '@/components/boxes/objectBoxes/interfaces.ts';
import { CustomCommissurePointsBoxController } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/CustomCommissurePointsBox.controller.ts';
import { BoxIndicationStateStore } from '@/components/boxes/common/BoxIndicationStateStore/BoxIndicationStateStore.ts';
import { CustomCommissurePointsIndicationController } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/CustomCommissurePointsIndication.controller.ts';
import { CommissurePointsResolver } from '@/engine/app/CommissurePointsResolver';
import { ThemeStore } from '@/assets/branding/Theme.store';
import { coreModule } from '../app/modules/core';
import { offlineIocModule } from '../app/modules/offline';
import { CaseDataRepositoryFactory } from '../app/CasePersistance/CaseDataRepositoryFactory';
import { PackageInfoStore } from '../app/CasePersistance/PackageInfoStore';
import { UserPreferencesStore } from '../stores/UserPreferences.store';
import { ViewOptionsCommandFactory } from '@/components/viewOption/commands/ViewOptionsCommand.factory';
import { StepsListCommandsFactory } from '@/components/stepsList/commands/StepsListCommands.factory';
import { UndoRedoButtonsController } from '@/components/header/UndoRedoButtons/UndoRedoButtons.controller';
import { NavigationButtonsController } from '@/components/navigationButtons/NavigationButtons.controller';
import { SynchronizeViewsController } from '@/components/viewsSynchronization/SynchronizeViewsController';

import { WindowKeyboardShortcutController } from '@/engine/Controllers/windowKeyboardShortcutController/windowKeyboardShortcut.controller.ts';
import {
  EntitiesVisibilityTogglingService,
  type IEntitiesVisibilityTogglingService
} from '@/engine/keyboardShortcutServices/entitiesVisibilityToggling.service.ts';
import type { IWindowKeyboardShortcutController } from '@/engine/Controllers/interfaces.ts';
import { learningGuideModule } from '@/engine/app/modules/learningGuide/ioc/ioc.module';
import { SynchronizeTabsController } from '@/components/viewsSynchronization/SynchronizeTabsController';
import { OrientationPresetsCommandsFactory } from '@/components/orientationPresets/commands/OrientationPresetCommands.factory';
import { NumberTuneCommandsFactory } from '../PlanningAlgorithm/Tuners/commands/NumberTuneCommands.factory';
import { IdentifierTuneCommandsFactory } from '../PlanningAlgorithm/Tuners/commands/IdentifiersListTuneCommands.factory';
import type { IWideAreaMeasurementConfigStrategy } from '@/components/boxes/measurementBoxes/wideAreaMeasurement/interface.ts';
import { WideAreaMeasurementConfigStrategy } from '@/components/boxes/measurementBoxes/wideAreaMeasurement/wideAreaMeasurementStrategy/WideAreaMeasurementConfig.strategy.ts';
import { DiametersCommandsFactory } from '@/components/boxes/measurementBoxes/commands/DiametersCommands.factory';
import { WorkflowIndicationsResolver } from '@/components/boxes/indicationsBoxes/workflowIndications/workflowIndicationsResolver.ts';
import type { IBoxViewModelModifier, IBoxIndicationStateStore } from '@/components/boxes/common/interfaces.ts';
import { BoxViewModelModifier } from '@/components/boxes/common/boxViewModelModifier.ts';
import { BooleanTuneCommandsFactory } from '../PlanningAlgorithm/Tuners/commands/BooleanTuneCommands.factory';
import { Remover } from '../UIPresentation/Remover';
import type { IRemover } from '../UIPresentation/Interfaces';
import type { ITuneCommandFactory } from '../PlanningAlgorithm/Tuners/commands/interfaces';
import type { ICommand } from '../app/modules/core/interfaces/Command';
import type { BooleanTuner } from '../PlanningAlgorithm/Tuners/BooleanTuner';
import type { IdentifiersListTuner } from '../PlanningAlgorithm/Tuners/IdentifiersListTuner';
import type { NumberTuner } from '../PlanningAlgorithm/Tuners/NumberTuner';
import { CommissureCommandsFactory } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/commands/CommissureCommands.factory';
import type { ICommissureCommandsFactory } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/interfaces';
import { VisibilityLinkedVMController } from '@/components/viewOption/commands/VisibilityLinkedVMController';
import { CineloopControlController } from '@/components/phasesCurve/cineloopControlController';
import { ToastNotificationsController } from '@/components/toastNotifications/ToastNotifications.controller';
import { NotificationsCenter } from '../app/NotificationsCenter';
import { VariableDistanceMeasurementBoxResolver } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/VariableDistanceMeasurementBox.resolver.ts';
import { PapillaryIndicationController } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/indicationControllers/PapillaryIndication.controller.ts';
import { LeafletsIndicationController } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/indicationControllers/LeafletsIndication.controller.ts';
import { VariableDistanceCommandsFactory } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/commands/VariableDistanceCommandsFactory.ts';
import { AccentedQuadroViewLayoutController } from '@/components/layout/quadroLayout/accentedQuadroLayout/AccentedQuadroViewLayoutController';
import { SymmetricalQuadroViewLayoutController } from '@/components/layout/quadroLayout/symmetricalQuadroLayout/SymmetricalQuadroViewLayoutController';
import { QuadroLayoutStateStore } from '@/components/layout/quadroLayout/QuadroLayoutState.store';
import { QuadroLayoutController } from '@/components/layout/quadroLayout/QuadroLayout.controller';
import { CustomCommissurePointsDescriptorResolver } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/CustomCommissurePointsDescriptorResolver';
import { IndicationColorSetter } from '@/components/boxes/IndicationColorSetter';
import { UserPreferencesPersistenceApi } from '../stores/UserPreferencesPersistenceApi';
import { CancellationController } from '../Controllers/Cancellation.controller';
import { BannerStore } from '@/engine/app/banner/Banner.store';
import { BannerController } from '@/engine/app/banner/Banner.controller';
import { BannerService } from '@/engine/app/banner/Banner.service';

const iocContainer: Container = new Container();

iocContainer.load(imagesModule);
iocContainer.load(indicationModule);
iocContainer.load(httpModule);
iocContainer.load(reportingModule);
iocContainer.load(coreModule);
iocContainer.load(offlineIocModule);
iocContainer.load(learningGuideModule);

iocContainer
  .bind<IAnatomyOrientationController>(AnatomyOrientationTYPES.IAnatomyOrientationController)
  .to(AnatomyOrientationController)
  .inSingletonScope();

iocContainer.bind<ICubeControllerFactory>(CubeTYPES.ICubeControllerFactory).to(CubeControllerFactory);
iocContainer.bind<ICubeSceneController>(CubeTYPES.ICubeSceneController).to(CubeSceneController);
iocContainer.bind<ICubeButtonsController>(CubeTYPES.ICubeButtonsController).to(CubeButtonsController);
iocContainer.bind<ICubePresentationBuilder>(CubeTYPES.ICubePresentationBuilder).to(CubePresentationBuilder);

iocContainer
  .bind<IFluoroAnglesCalculatorFactory>(FluoroAngleTYPES.IFluoroAnglesCalculatorFactory)
  .to(FluoroAnglesCalculatorFactory);

iocContainer
  .bind<INavigationControllerFactory>(NavigationTYPES.INavigationControllerFactory)
  .to(NavigationControllerFactory);

iocContainer.bind<IWasmModulesRegistry>(TYPES.IWasmModulesRegistry).to(WasmModulesRegistry).inSingletonScope();

iocContainer.bind<ViewsService>(TYPES.ViewsService).to(ViewsService).inSingletonScope();

iocContainer.bind<ViewsListController>(TYPES.ViewsListController).to(ViewsListController);

iocContainer.bind<PointsSwitcherController>(TYPES.PointsSwitcherController).to(PointsSwitcherController);

iocContainer.bind<StepsListController>(TYPES.StepsListController).to(StepsListController);

iocContainer.bind<CurvedMprController>(TYPES.CurvedMprController).to(CurvedMprController).inSingletonScope();

iocContainer.bind<ApproveController>(TYPES.ApproveController).to(ApproveController);

iocContainer.bind<NavigationButtonsController>(TYPES.NavigationButtonsController).to(NavigationButtonsController);

iocContainer.bind<AppController>(TYPES.AppController).to(AppController).inSingletonScope();

iocContainer.bind<ICineloopInfo>(TYPES.ICineloopInfo).toService(TYPES.AppController);

iocContainer
  .bind<ToastNotificationsController>(TYPES.ToastNotificationsController)
  .to(ToastNotificationsController)
  .inSingletonScope();

iocContainer.bind<INotificationsCenter>(TYPES.NotificationsCenter).to(NotificationsCenter).inSingletonScope();

iocContainer.bind<IFrameController>(TYPES.IFrameController).to(IFrameController).inSingletonScope();

iocContainer.bind<IViewPortManagerBuilder>(TYPES.IViewPortManagerBuilder).to(ViewPortManagerBuilder).inSingletonScope();

iocContainer.bind<IIlluminator>(TYPES.IIlluminator).to(Illuminator);

iocContainer
  .bind<IViewPortControllerFactory>(TYPES.IViewPortControllerFactory)
  .to(ViewPortControllerFactory)
  .inSingletonScope();

iocContainer.bind<ViewController>(TYPES.ViewController).to(ViewController).inSingletonScope();

iocContainer
  .bind<interfaces.Factory<ViewController>>(TYPES.ViewControllerProvider)
  .toAutoFactory<ViewController>(TYPES.ViewController);

iocContainer.bind<PlanningAlgorithm>(TYPES.PlanningAlgorithm).to(PlanningAlgorithm).inSingletonScope();

iocContainer.bind<CasePhaseData>(TYPES.CasePhaseData).to(CasePhaseData).inSingletonScope();

iocContainer.bind<PlanningEntityRegistry>(TYPES.PlanningEntityRegistry).to(PlanningEntityRegistry).inSingletonScope();

iocContainer
  .bind<DynamicPlanningAlgorithmPromoter>(TYPES.DynamicPlanningAlgorithmMediator)
  .to(DynamicPlanningAlgorithmPromoter)
  .inSingletonScope();

iocContainer.bind<IndicationsResolver>(TYPES.IndicationsResolver).to(IndicationsResolver).inSingletonScope();

iocContainer
  .bind<IStrategyByKindProvider>(TYPES.IStrategyByKindProvider)
  .to(LoadStateStrategiesProvider)
  .inSingletonScope();

iocContainer
  .bind<DiameterMeasurementsResolver>(TYPES.DiameterMeasurementsResolver)
  .to(DiameterMeasurementsResolver)
  .inSingletonScope();

iocContainer
  .bind<DistanceMeasurementsResolver>(TYPES.DistanceMeasurementsResolver)
  .to(DistanceMeasurementsResolver)
  .inSingletonScope();

iocContainer
  .bind<CommissurePointsResolver>(TYPES.CommissurePointsResolver)
  .to(CommissurePointsResolver)
  .inSingletonScope();

iocContainer
  .bind<OrientationPresetsResolver>(TYPES.OrientationPresetsResolver)
  .to(OrientationPresetsResolver)
  .inSingletonScope();

iocContainer.bind<ImporterFactory>(TYPES.ImporterFactory).to(ImporterFactory).inSingletonScope();

iocContainer
  .bind<CasePersistenceRegistrar>(TYPES.CasePersistenceRegistrar)
  .to(CasePersistenceRegistrar)
  .inSingletonScope();

iocContainer.bind<CasePersistenceHub>(TYPES.CasePersistenceHub).to(CasePersistenceHub).inSingletonScope();

iocContainer.bind<IViewFactoryProvider<JSONConfig.Config>>(TYPES.IViewFactoryProvider).to(ViewFactoryProvider);

iocContainer.bind<ISceneItemDetailsFactory>(TYPES.ISceneItemDetailsFactory).to(SceneItemDetailsFactory);

iocContainer.bind<ObjectsListController>(TYPES.ObjectsListController).to(ObjectsListController);

iocContainer
  .bind<OrientationPresetsController>(TYPES.OrientationPresetsController)
  .to(OrientationPresetsController)
  .inSingletonScope();

iocContainer
  .bind<IFluoroCurveControllerFactory>(FluoroCurveTYPES.IFluoroCurveControllerFactory)
  .to(FluoroCurveControllerFactory);

iocContainer.bind<AreaDiametersController>(TYPES.AreaDiametersController).to(AreaDiametersController);

iocContainer
  .bind<VariableDistancesBoxController>(TYPES.VariableDistancesBoxController)
  .to(VariableDistancesBoxController);

iocContainer
  .bind<VariableDistanceMeasurementBoxResolver>(TYPES.VariableDistanceMeasurementBoxResolver)
  .to(VariableDistanceMeasurementBoxResolver)
  .inSingletonScope();

iocContainer.bind<PapillaryIndicationController>(TYPES.PapillaryIndicationController).to(PapillaryIndicationController);

iocContainer.bind<LeafletsIndicationController>(TYPES.LeafletsIndicationController).to(LeafletsIndicationController);

iocContainer
  .bind<ICustomCommissurePointsBoxController>(TYPES.CustomCommissurePointsBoxController)
  .to(CustomCommissurePointsBoxController);

iocContainer
  .bind<CustomCommissurePointsDescriptorResolver>(TYPES.CustomCommissurePointsDescriptorResolver)
  .to(CustomCommissurePointsDescriptorResolver);

iocContainer
  .bind<CustomCommissurePointsIndicationController>(TYPES.CustomCommissurePointsIndicationController)
  .to(CustomCommissurePointsIndicationController)
  .inSingletonScope();

iocContainer.bind<IBoxIndicationStateStore>(TYPES.BoxIndicationStateStore).to(BoxIndicationStateStore);

iocContainer
  .bind<SynchronizeViewsController>(TYPES.SynchronizeViewsController)
  .to(SynchronizeViewsController)
  .inSingletonScope();

iocContainer
  .bind<SynchronizeTabsController>(TYPES.SynchronizeTabsController)
  .to(SynchronizeTabsController)
  .inSingletonScope();

iocContainer.bind<ViewOptionsController>(TYPES.ViewOptionsController).to(ViewOptionsController);

iocContainer.bind<ImageSlicesListController>(TYPES.ImageSlicesListController).to(ImageSlicesListController);

iocContainer.bind<IMeasurementsBoxesController>(TYPES.IMeasurementsBoxesController).to(MeasurementBoxesController);

iocContainer.bind<IBoxViewModelModifier>(TYPES.IBoxViewModelModifier).to(BoxViewModelModifier);

iocContainer.bind<IndicationsBoxController>(TYPES.IndicationsBoxController).to(IndicationsBoxController);

iocContainer.bind<IObjectBoxesController>(TYPES.IObjectBoxesController).to(ObjectBoxesController);

iocContainer.bind<IViewItemsViewModelController>(TYPES.IViewItemsViewModelController).to(ViewItemsViewModelController);

iocContainer.bind<SavingStateController>(TYPES.SavingStateController).to(SavingStateController).inSingletonScope();

iocContainer.bind<CaseInfoApi>(TYPES.CaseInfoApi).to(CaseInfoApi).inSingletonScope();

iocContainer.bind<CaseFilesApi>(TYPES.CaseFilesApi).to(CaseFilesApi).inSingletonScope();

iocContainer.bind<CaseDataProvider>(TYPES.CaseDataProvider).to(CaseDataProvider).inSingletonScope();

iocContainer.bind<AppBootstrapper>(TYPES.AppBootstrapper).to(AppBootstrapper);

iocContainer.bind<AuthInterceptor>(TYPES.AuthInterceptor).to(AuthInterceptor);

iocContainer.bind<ITwoDLayerController>(TYPES.TwoDLayerController).to(TwoDLayerController);

iocContainer.bind<SurgeryRegistry>(TYPES.SurgeryRegistry).to(SurgeryRegistry).inSingletonScope();

iocContainer.bind<IPhasesCurveController>(TYPES.IPhasesCurveController).to(PhasesCurveController).inSingletonScope();

iocContainer
  .bind<ICineloopControlController>(TYPES.ICineloopControlController)
  .to(CineloopControlController)
  .inSingletonScope();

iocContainer.bind<UIConfigResolver>(TYPES.UIConfigResolver).to(UIConfigResolver).inSingletonScope();

iocContainer
  .bind<PresentationCreatorsRegistry>(TYPES.PresentationCreatorsRegistry)
  .to(PresentationCreatorsRegistry)
  .inSingletonScope();

iocContainer.bind<ThemeStore>(TYPES.ThemeStore).to(ThemeStore).inSingletonScope();

iocContainer.bind<ICaseDataRepositoryFactory>(TYPES.ICaseDataRepositoryFactory).to(CaseDataRepositoryFactory);

iocContainer.bind<PackageInfoStore>(TYPES.PackageInfoStore).to(PackageInfoStore).inSingletonScope();

iocContainer
  .bind<IEntitiesVisibilityTogglingService>(TYPES.EntitiesVisibilityTogglingService)
  .to(EntitiesVisibilityTogglingService)
  .inSingletonScope();

iocContainer
  .bind<IWindowKeyboardShortcutController>(TYPES.WindowKeyboardShortcutController)
  .to(WindowKeyboardShortcutController)
  .inSingletonScope();

iocContainer
  .bind<IWideAreaMeasurementConfigStrategy>(TYPES.WideAreaMeasurementConfigStrategy)
  .to(WideAreaMeasurementConfigStrategy)
  .inSingletonScope();

iocContainer
  .bind<WorkflowIndicationsResolver>(TYPES.WorkflowIndicationsResolver)
  .to(WorkflowIndicationsResolver)
  .inSingletonScope();

iocContainer
  .bind<UserPreferencesStore<unknown>>(TYPES.UserPreferencesStore)
  .to(UserPreferencesStore)
  .inSingletonScope();

iocContainer
  .bind<VisibilityLinkedVMController>(TYPES.VisibilityLinkedVMController)
  .to(VisibilityLinkedVMController)
  .inSingletonScope();

iocContainer.bind<ViewOptionsCommandFactory>(TYPES.ViewOptionsCommandFactory).to(ViewOptionsCommandFactory);

iocContainer.bind<StepsListCommandsFactory>(TYPES.StepsListCommandsFactory).to(StepsListCommandsFactory);

/*iocContainer.bind<IdentifierTuneCommandsFactory>(TYPES.IdentifierTuneCommandsFactory).to(IdentifierTuneCommandsFactory);

iocContainer.bind<BooleanTuneCommandsFactory>(TYPES.BooleanTuneCommandsFactory).to(BooleanTuneCommandsFactory);

iocContainer.bind<NumberTuneCommandsFactory>(TYPES.NumberTuneCommandsFactory).to(NumberTuneCommandsFactory);*/

iocContainer
  .bind<ITuneCommandFactory<ICommand, IdentifiersListTuner, string>>(TYPES.IdentifierTuneCommandsFactory)
  .to(IdentifierTuneCommandsFactory);

iocContainer
  .bind<ITuneCommandFactory<ICommand, BooleanTuner, boolean>>(TYPES.BooleanTuneCommandsFactory)
  .to(BooleanTuneCommandsFactory);

iocContainer
  .bind<ITuneCommandFactory<ICommand, NumberTuner, number>>(TYPES.NumberTuneCommandsFactory)
  .to(NumberTuneCommandsFactory);

iocContainer
  .bind<OrientationPresetsCommandsFactory>(TYPES.OrientationPresetsCommandsFactory)
  .to(OrientationPresetsCommandsFactory);

iocContainer.bind<DiametersCommandsFactory>(TYPES.DiametersCommandsFactory).to(DiametersCommandsFactory);

iocContainer
  .bind<VariableDistanceCommandsFactory>(TYPES.VariableDistanceCommandsFactory)
  .to(VariableDistanceCommandsFactory);

iocContainer.bind<ICommissureCommandsFactory>(TYPES.ICommissureCommandsFactory).to(CommissureCommandsFactory);

iocContainer.bind<UndoRedoButtonsController>(TYPES.UndoRedoButtonsController).to(UndoRedoButtonsController);

iocContainer.bind<IRemover>(TYPES.IRemover).to(Remover);

iocContainer
  .bind<AccentedQuadroViewLayoutController>(TYPES.AccentedQuadroViewLayoutController)
  .to(AccentedQuadroViewLayoutController)
  .inSingletonScope();

iocContainer
  .bind<SymmetricalQuadroViewLayoutController>(TYPES.SymmetricalQuadroViewLayoutController)
  .to(SymmetricalQuadroViewLayoutController)
  .inSingletonScope();

iocContainer.bind<QuadroLayoutStateStore>(TYPES.QuadroLayoutStateStore).to(QuadroLayoutStateStore).inSingletonScope();

iocContainer.bind<QuadroLayoutController>(TYPES.QuadroLayoutController).to(QuadroLayoutController).inSingletonScope();

iocContainer.bind<IndicationColorSetter>(TYPES.IndicationColorSetter).to(IndicationColorSetter);

iocContainer
  .bind<UserPreferencesPersistenceApi>(TYPES.UserPreferencesPersistenceApi)
  .to(UserPreferencesPersistenceApi)
  .inSingletonScope();

iocContainer.bind<CancellationController>(TYPES.CancellationController).to(CancellationController).inSingletonScope();

iocContainer.bind<BannerStore>(TYPES.BannerStore).to(BannerStore).inSingletonScope();

iocContainer.bind<BannerController>(TYPES.BannerController).to(BannerController);

iocContainer.bind<BannerService>(TYPES.BannerService).to(BannerService).inSingletonScope();

export { iocContainer };
